import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { useSaveListing, useAmazonAccounts } from '../../features/amazon/hooks';
import { Button } from '../../components/common/Button';
import { BackButton } from '../../components/common/BackButton';
import { Label } from '../../components/common/Label';
import type { LabelVariant } from '../../components/common/Label';
import { BulkActionsBar } from '../../components/common/BulkActionsBar';
import { AuthErrorBanner } from '../../components/shared/AuthErrorBanner';
import '../../components/common/CommonTable.css';
import './ProductsPageHeader.css';
import './ProductSearchPage.css';

interface SellerListing {
  asin: string;
  sku: string;
  itemName: string;
  status: string[];
  mainImageUrl: string | null;
}

interface PageCache {
  listings: SellerListing[];
  nextToken: string | null;
}

const SELECTED_ACCOUNT_KEY = 'selectedAmazonAccountId';
const STATUS_FILTER_KEY = 'amazonListings_statusFilter';
const UPDATED_AFTER_FILTER_KEY = 'amazonListings_updatedAfterFilter';
const SYNC_FILTER_KEY = 'amazonListings_syncFilter';
const SORT_BY_KEY = 'amazonListings_sortBy';
const SORT_ORDER_KEY = 'amazonListings_sortOrder';

interface AuthError {
  error?: string;
  message?: string;
  action?: 'reauthenticate' | 'contact_admin';
  redirect_url?: string;
}

export default function AmazonListingsPage() {
  const navigate = useNavigate();
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  // セラーの商品一覧表示用の状態
  const [listings, setListings] = useState<SellerListing[]>([]);
  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [isFetchingListings, setIsFetchingListings] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [savedSkus, setSavedSkus] = useState<Set<string>>(new Set());
  const [pageHistory, setPageHistory] = useState<PageCache[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  // フィルタとソートの状態
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem(STATUS_FILTER_KEY) || 'all';
  });
  const [updatedAfterFilter, setUpdatedAfterFilter] = useState(() => {
    return localStorage.getItem(UPDATED_AFTER_FILTER_KEY) || 'none';
  });
  const [syncFilter, setSyncFilter] = useState(() => {
    return localStorage.getItem(SYNC_FILTER_KEY) || 'all';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem(SORT_BY_KEY) || 'lastUpdatedDate';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem(SORT_ORDER_KEY) || 'DESC';
  });

  // Amazon Seller Accountsを取得
  const { accounts, isLoading: isLoadingAccounts } = useAmazonAccounts();

  // 選択されたアカウントの状態を取得
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);
  const isAccountExpired = selectedAccount?.status === 'expired';

  // 商品保存のmutation
  const saveListingMutation = useSaveListing();

  // 初回ロード時にlocalStorageから前回選択したアカウントを復元
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (savedAccountId && accounts.find((acc) => acc.id === savedAccountId)) {
        setSelectedAccountId(savedAccountId);
      } else {
        // 保存されたアカウントが無効な場合、最初のアカウントを選択
        setSelectedAccountId(accounts[0].id);
        localStorage.setItem(SELECTED_ACCOUNT_KEY, accounts[0].id);
      }
    }
  }, [accounts, selectedAccountId]);

  // アカウントが期限切れの場合、authErrorをセット
  useEffect(() => {
    if (isAccountExpired) {
      // 現時点では全ユーザーをadminとして扱う（TODO: 実際のユーザーロールを取得）
      setAuthError({
        error: 'Amazon認証の有効期限が切れています',
        message: 'Amazon Seller Centralとの接続を再認証する必要があります。',
        action: 'reauthenticate',
        redirect_url: '/connections/amazon'
      });
    } else {
      setAuthError(null);
    }
  }, [isAccountExpired]);

  // フィルタ値が変更されたらlocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STATUS_FILTER_KEY, statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem(UPDATED_AFTER_FILTER_KEY, updatedAfterFilter);
  }, [updatedAfterFilter]);

  useEffect(() => {
    localStorage.setItem(SYNC_FILTER_KEY, syncFilter);
  }, [syncFilter]);

  useEffect(() => {
    localStorage.setItem(SORT_BY_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem(SORT_ORDER_KEY, sortOrder);
  }, [sortOrder]);

  // アカウント、ページサイズ、またはフィルタが変更されたら自動で商品一覧を取得
  useEffect(() => {
    if (selectedAccountId) {
      handleFetchListings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, pageSize, statusFilter, updatedAfterFilter, syncFilter, sortBy, sortOrder]);

  // アカウント選択が変更されたらlocalStorageに保存
  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
    // handleFetchListingsはuseEffectで自動実行される
  };

  const handleStartAuth = () => {
    // OAuth認証ページに遷移
    navigate('/connections/amazon');
  };

  // 既存商品をチェックしてsavedSkusを更新
  const checkExistingProducts = async (listings: SellerListing[], replaceAll = false) => {
    const validSkus = listings
      .map(l => l.sku)
      .filter(sku => sku && sku !== 'null' && sku !== 'undefined');

    if (validSkus.length === 0) {
      return;
    }

    try {
      const response = await apiClient.post('/amazon/products/check-existing', {
        skus: validSkus,
      });

      const existingSkus = response.data.existing_skus || [];

      if (replaceAll) {
        setSavedSkus(new Set(existingSkus));
      } else {
        // 既存のsavedSkusに追加
        setSavedSkus(prev => new Set([...prev, ...existingSkus]));
      }
    } catch (err) {
      console.error('[checkExistingProducts] Failed to check existing products:', err);
      // エラーが発生しても処理は継続
    }
  };

  // 更新日フィルタをISO 8601形式に変換
  const getUpdatedAfterDate = (filter: string): string | undefined => {
    if (filter === 'none') return undefined;

    const now = new Date();
    switch (filter) {
      case '24h':
        now.setHours(now.getHours() - 24);
        break;
      case '7d':
        now.setDate(now.getDate() - 7);
        break;
      case '30d':
        now.setDate(now.getDate() - 30);
        break;
      default:
        return undefined;
    }
    return now.toISOString();
  };

  const handleFetchListings = async () => {
    if (!selectedAccountId) {
      setError('Amazon Seller Accountを選択してください');
      return;
    }

    // アカウントが期限切れの場合はAPIを呼ばない
    if (isAccountExpired) {
      return;
    }

    setIsFetchingListings(true);
    setError(null);
    setListings([]);
    setSelectedAsins(new Set());
    setPageHistory([]);
    setCurrentPageIndex(0);

    try {
      const params: any = {
        account_id: selectedAccountId,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        sync_filter: syncFilter,
      };

      // ステータスフィルタ
      if (statusFilter !== 'all') {
        params.with_status = statusFilter === 'buyable' ? 'BUYABLE' : 'DISCOVERABLE';
      }

      // 更新日フィルタ
      const updatedAfter = getUpdatedAfterDate(updatedAfterFilter);
      if (updatedAfter) {
        params.last_updated_after = updatedAfter;
      }

      const response = await apiClient.get('/amazon/listings', { params });

      const firstPage: PageCache = {
        listings: response.data.listings || [],
        nextToken: response.data.nextToken || null,
      };

      // 既存商品をチェック（最初のページなので全置換）
      await checkExistingProducts(firstPage.listings, true);

      // チェック完了後にテーブルを表示
      setListings(firstPage.listings);
      setPageHistory([firstPage]);
    } catch (err: any) {
      // 認証エラーの場合
      if (err.response?.status === 401 && err.response?.data?.action) {
        setAuthError(err.response.data);
        setError(null);
      } else {
        setError(err.response?.data?.error || '商品一覧の取得に失敗しました');
        setAuthError(null);
      }
    } finally {
      setIsFetchingListings(false);
    }
  };

  const handleNextPage = async () => {
    if (!selectedAccountId) {
      return;
    }

    // アカウントが期限切れの場合はAPIを呼ばない
    if (isAccountExpired) {
      return;
    }

    const currentPage = pageHistory[currentPageIndex];
    if (!currentPage || !currentPage.nextToken) {
      return;
    }

    // 既に次のページがキャッシュされている場合
    if (currentPageIndex < pageHistory.length - 1) {
      const nextPage = pageHistory[currentPageIndex + 1];
      setListings(nextPage.listings);
      setCurrentPageIndex(prev => prev + 1);
      return;
    }

    // 新しいページを取得
    setIsLoadingPage(true);
    setError(null);

    try {
      // Rate limitを避けるため、少し待機
      await new Promise(resolve => setTimeout(resolve, 500));

      const params: any = {
        account_id: selectedAccountId,
        page_token: currentPage.nextToken,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        sync_filter: syncFilter,
      };

      // ステータスフィルタ
      if (statusFilter !== 'all') {
        params.with_status = statusFilter === 'buyable' ? 'BUYABLE' : 'DISCOVERABLE';
      }

      // 更新日フィルタ
      const updatedAfter = getUpdatedAfterDate(updatedAfterFilter);
      if (updatedAfter) {
        params.last_updated_after = updatedAfter;
      }

      const response = await apiClient.get('/amazon/listings', { params });

      const newPage: PageCache = {
        listings: response.data.listings || [],
        nextToken: response.data.nextToken || null,
      };

      // 既存商品をチェック（追加モード）
      await checkExistingProducts(newPage.listings, false);

      // チェック完了後にテーブルを表示
      setListings(newPage.listings);
      setPageHistory(prev => [...prev, newPage]);
      setCurrentPageIndex(prev => prev + 1);

      // 選択状態は保持する（ページをまたいだ選択を可能にする）
    } catch (err: any) {
      // 認証エラーの場合
      if (err.response?.status === 401 && err.response?.data?.action) {
        setAuthError(err.response.data);
        setError(null);
      } else {
        setError(err.response?.data?.error || '商品一覧の取得に失敗しました');
        setAuthError(null);
      }
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePrevPage = () => {
    if (currentPageIndex === 0) {
      return;
    }

    const prevPage = pageHistory[currentPageIndex - 1];
    setListings(prevPage.listings);
    setCurrentPageIndex(prev => prev - 1);
  };

  const handleToggleAsin = (asin: string) => {
    const newSelected = new Set(selectedAsins);
    if (newSelected.has(asin)) {
      newSelected.delete(asin);
    } else {
      newSelected.add(asin);
    }
    setSelectedAsins(newSelected);
  };

  const handleToggleAll = () => {
    if (selectedAsins.size === listings.length) {
      setSelectedAsins(new Set());
    } else {
      setSelectedAsins(new Set(listings.map((l) => l.asin)));
    }
  };

  // ステータスラベル変換
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'BUYABLE':
        return '購入可能';
      case 'DISCOVERABLE':
        return '表示可能';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): LabelVariant => {
    switch (status) {
      case 'BUYABLE':
        return 'green';
      case 'DISCOVERABLE':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const handleImportSelected = async () => {
    if (selectedAsins.size === 0) {
      setError('取り込む商品を選択してください');
      return;
    }

    if (!selectedAccountId) {
      setError('Amazon Seller Accountを選択してください');
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      // 選択されたASINに対応するlistingを取得
      const selectedListings = listings.filter((listing) =>
        selectedAsins.has(listing.asin)
      );

      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // 各商品を個別に保存（SKU付きで保存）
      for (const listing of selectedListings) {
        try {
          // SKUのバリデーション
          if (!listing.sku || listing.sku === 'null' || listing.sku === 'undefined') {
            errorCount++;
            errors.push(`${listing.asin}: SKUが設定されていません`);
            continue;
          }

          await saveListingMutation.mutateAsync({
            sku: listing.sku,
            asin: listing.asin,
            accountId: selectedAccountId,
          });

          successCount++;
          setSavedSkus((prev) => new Set([...prev, listing.sku]));
        } catch (err: any) {
          errorCount++;
          const errorMsg = err.response?.data?.error || '保存に失敗しました';
          errors.push(`${listing.asin}: ${errorMsg}`);
        }
      }

      if (errorCount > 0) {
        setError(
          `${successCount}件成功、${errorCount}件失敗しました。\n${errors.slice(0, 5).join('\n')}${
            errors.length > 5 ? '\n...' : ''
          }`
        );
      } else {
        // 全て成功したら選択を解除
        setSelectedAsins(new Set());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '一括取り込みに失敗しました');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSaveListing = async (sku: string, asin: string) => {
    if (!selectedAccountId) {
      setError('Amazon Seller Accountを選択してください');
      return;
    }

    // SKUのバリデーション
    if (!sku || sku === 'null' || sku === 'undefined') {
      setError(`SKUが設定されていない商品です (ASIN: ${asin})。Amazon Seller Centralで商品を確認してください。`);
      return;
    }

    setError(null);

    try {
      await saveListingMutation.mutateAsync({
        sku,
        asin,
        accountId: selectedAccountId,
      });

      // 成功したらsavedSkusに追加
      setSavedSkus(prev => new Set([...prev, sku]));

      // 成功メッセージを一時的に表示
      const successMessage = `SKU: ${sku} を保存しました`;
      setError(null);

      // 一時的な成功通知（3秒後に消す）
      const tempDiv = document.createElement('div');
      tempDiv.textContent = successMessage;
      tempDiv.style.cssText = 'position: fixed; top: 20px; right: 20px; background: rgba(34, 197, 94, 0.9); color: white; padding: 12px 20px; border-radius: 8px; z-index: 9999;';
      document.body.appendChild(tempDiv);
      setTimeout(() => tempDiv.remove(), 3000);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error) {
        let errorMessage = errorData.error;
        if (errorData.details) {
          errorMessage += `\n詳細: ${errorData.details}`;
        }
        if (errorData.solution) {
          errorMessage += `\nヒント: ${errorData.solution}`;
        }
        setError(errorMessage);
      } else {
        setError('商品の保存に失敗しました');
      }
    }
  };

  return (
    <div className="product-search-page" style={{ marginBottom: selectedAsins.size > 0 ? '33px' : '0' }}>
      <div className="header-actions">
        <BackButton to="/products/amazon" />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">Amazon Seller Partner APIと商品データを連携</h1>
          <p className="page-description">Seller Partner APIから商品一覧を取得して、データベースに連携します</p>
        </div>
      </header>

      <section className="search-section">
        {isLoadingAccounts ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>アカウント情報を読み込み中...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="account-register-form">
            <h3>Amazon Seller Account連携</h3>
            <p className="form-description">
              Amazon Sellerアカウントと連携して、商品情報を取得できます
            </p>
            <div className="form-actions form-actions-centered">
              <Button
                type="button"
                onClick={handleStartAuth}
                variant="primary"
                size="md"
              >
                Amazonアカウントと連携
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* 認証エラーバナー */}
            {authError && <AuthErrorBanner error={authError} />}

            {/* 一般エラーメッセージ */}
            {error && !authError && (
              <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                {error}
              </div>
            )}

            {/* メイン: 商品一覧から取り込む */}
            <div className="main-section">

              {/* アカウント選択行 (デスクリプションとフィルタの間に配置) */}
              <div className="account-selector-row">
                <label htmlFor="account-select">Amazon Seller Account</label>
                <select
                  id="account-select"
                  value={selectedAccountId}
                  onChange={(e) => handleAccountChange(e.target.value)}
                  className="form-select"
                >
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.seller_id} ({account.marketplace_id})
                    </option>
                  ))}
                </select>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/connections/amazon');
                  }}
                  className="text-link"
                >
                  <img src="/cog.svg" alt="" className="text-link-icon" />
                  接続管理
                </a>
              </div>

              <section className="list-controls">
                <div className="controls-row">
                  <div className="filter-section">
                    <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
                    <span className="filter-label">フィルタ</span>
                    <div className="filter-group">
                      <label htmlFor="status-filter">ステータス</label>
                      <select
                        id="status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`form-select ${statusFilter !== 'all' ? 'filter-active' : ''}`}
                      >
                        <option value="all">すべて</option>
                        <option value="buyable">購入可能</option>
                        <option value="discoverable">表示可能</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="updated-after-filter">更新日</label>
                      <select
                        id="updated-after-filter"
                        value={updatedAfterFilter}
                        onChange={(e) => setUpdatedAfterFilter(e.target.value)}
                        className={`form-select ${updatedAfterFilter !== 'none' ? 'filter-active' : ''}`}
                      >
                        <option value="none">すべて</option>
                        <option value="24h">24時間以内</option>
                        <option value="7d">7日以内</option>
                        <option value="30d">30日以内</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="sync-filter">連携状態</label>
                      <select
                        id="sync-filter"
                        value={syncFilter}
                        onChange={(e) => setSyncFilter(e.target.value)}
                        className={`form-select ${syncFilter !== 'all' ? 'filter-active' : ''}`}
                      >
                        <option value="all">すべて</option>
                        <option value="unsynced">未連携</option>
                        <option value="synced">連携済み</option>
                      </select>
                    </div>
                    <div className="total-count">
                      全 <strong>{listings.length}</strong> 件（このページ）
                    </div>
                  </div>
                  <div className="sort-section">
                    <img src="/arrange_letter.svg" alt="並び替え" className="sort-icon" />
                    <div className="sort-control">
                      <select
                        id="sort-select"
                        value={`${sortBy}_${sortOrder}`}
                        onChange={(e) => {
                          const [newSortBy, newSortOrder] = e.target.value.split('_');
                          setSortBy(newSortBy);
                          setSortOrder(newSortOrder);
                        }}
                        className="form-select"
                      >
                        <option value="lastUpdatedDate_DESC">最終更新日時（新しい順）</option>
                        <option value="lastUpdatedDate_ASC">最終更新日時（古い順）</option>
                        <option value="createdDate_DESC">作成日時（新しい順）</option>
                        <option value="createdDate_ASC">作成日時（古い順）</option>
                        <option value="sku_ASC">SKU（A-Z）</option>
                        <option value="sku_DESC">SKU（Z-A）</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>

          {listings.length > 0 && (
            <>
              <div className="table-container">
                <table className="listings-table common-table">
                  <thead>
                    <tr>
                      <th className="col-select">
                        <input
                          type="checkbox"
                          checked={selectedAsins.size === listings.length}
                          onChange={handleToggleAll}
                        />
                      </th>
                      <th className="col-image">画像</th>
                      <th className="col-asin">ASIN</th>
                      <th className="col-sku">SKU</th>
                      <th className="col-title">商品名</th>
                      <th className="col-status">ステータス</th>
                      <th className="col-actions">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((listing) => {
                      const isSaved = savedSkus.has(listing.sku);
                      const hasValidSku = listing.sku && listing.sku !== 'null' && listing.sku !== 'undefined';

                      return (
                        <tr key={listing.asin} style={!hasValidSku ? { backgroundColor: '#fff3cd' } : undefined}>
                          <td className="col-select">
                            <input
                              type="checkbox"
                              checked={selectedAsins.has(listing.asin)}
                              onChange={() => handleToggleAsin(listing.asin)}
                              disabled={!hasValidSku}
                            />
                          </td>
                          <td className="col-image">
                            {listing.mainImageUrl ? (
                              <img
                                src={listing.mainImageUrl}
                                alt={listing.itemName}
                                className="listing-thumbnail"
                              />
                            ) : (
                              <div className="listing-no-image">No Image</div>
                            )}
                          </td>
                          <td className="col-asin">{listing.asin}</td>
                          <td className="col-sku">
                            {hasValidSku ? (
                              listing.sku
                            ) : (
                              <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                SKU未設定
                              </span>
                            )}
                          </td>
                          <td className="col-title">{listing.itemName}</td>
                          <td className="col-status">
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {listing.status.map((status, index) => (
                                <Label key={index} variant={getStatusVariant(status)}>
                                  {getStatusLabel(status)}
                                </Label>
                              ))}
                            </div>
                          </td>
                          <td className="col-actions">
                            <div className="actions-group">
                            {hasValidSku ? (
                              <Button
                                type="button"
                                onClick={() => handleSaveListing(listing.sku, listing.asin)}
                                disabled={isSaved || saveListingMutation.isPending}
                                variant={isSaved ? 'muted' : 'primary'}
                                size="sm"
                              >
                                {isSaved ? '連携済み' : 'データ連携する'}
                              </Button>
                            ) : (
                              <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                連携不可
                              </span>
                            )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="listings-actions" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* 左側: 読み込み中ラベル */}
                <div style={{ flex: '1', display: 'flex', alignItems: 'center', minWidth: '150px' }}>
                  {(isLoadingPage || isFetchingListings) && (
                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      読み込み中...
                    </span>
                  )}
                </div>

                {/* 中央: ページネーション */}
                <div style={{ flex: '0 0 auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleFetchListings}
                    disabled={currentPageIndex === 0 || isLoadingPage || isFetchingListings}
                    className="pagination-text-button"
                  >
                    &lt;&lt; 最初
                  </button>
                  <button
                    type="button"
                    onClick={handlePrevPage}
                    disabled={currentPageIndex === 0 || isLoadingPage || isFetchingListings}
                    className="pagination-text-button"
                  >
                    &lt; 前
                  </button>
                  <span style={{ color: '#6c757d', fontSize: '0.875rem', minWidth: '80px', textAlign: 'center' }}>
                    ページ {currentPageIndex + 1}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={!pageHistory[currentPageIndex]?.nextToken || isLoadingPage || isFetchingListings}
                    className="pagination-text-button"
                  >
                    次 &gt;
                  </button>
                </div>

                {/* 右側: 表示件数 */}
                <div style={{ flex: '1', display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-end', minWidth: '150px' }}>
                  <label htmlFor="page-size-select" style={{ fontSize: '0.875rem', color: 'rgba(245, 241, 230, 0.8)', whiteSpace: 'nowrap' }}>
                    表示件数
                  </label>
                  <select
                    id="page-size-select"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="form-select"
                    style={{ width: '80px', fontSize: '0.875rem' }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {!isFetchingListings && listings.length === 0 && (
            <div className="empty-state" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(245, 241, 230, 0.6)' }}>
              <p>条件に一致する商品が見つかりませんでした</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>フィルタ条件を変更してお試しください</p>
            </div>
          )}

          {isFetchingListings && (
            <div className="loading-state" style={{ padding: '2rem', textAlign: 'center' }}>
              <p>読み込み中...</p>
            </div>
          )}

            {error && <div className="error-message">{error}</div>}
            </div>
          </>
        )}
      </section>

      {/* 選択中のアイテムがある場合、Sticky UIを表示 */}
      {selectedAsins.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedAsins.size}
          onCancel={() => setSelectedAsins(new Set())}
        >
          <button
            type="button"
            onClick={handleImportSelected}
            disabled={isImporting}
            className="btn-generate-primary"
          >
            {isImporting ? 'データ連携中...' : '選択した商品をデータ連携する'}
          </button>
        </BulkActionsBar>
      )}
    </div>
  );
}
